import { createContext, useContext } from 'react';

import type { ReactNode } from 'react';

export type ID = string | number;

export type Navigation<T extends ID = string> = {
  /**
   * current step id
   */
  activeId: T | null;
  /**
   * navigate to the next step
   */
  next: () => void;
  /**
   * navigate to previous step
   */
  back: () => void;
  /**
   * navigate to a specific step
   */
  goTo: (id: string) => void;
};

export const NavContext = createContext<Navigation | null>(null);

/**
 * hook for accessing the navigation context anywhere within the component tree
 *
 * @returns navigation context object
 */
export const useNavContext = <T extends ID>(): Navigation<T> => {
  const context = useContext(NavContext) as Navigation<T> | null;

  if (!context)
    throw new Error('useNavigationContext is used outside of NavigationContext.Provider');

  return context;
};

export type NavContextProviderProps<T extends ID> = Navigation<T> & {
  children?: ReactNode;
};

/**
 * manages the widget-x navigation state
 */
export const NavContextProvider = <T extends ID>({
  children,
  ...value
}: NavContextProviderProps<T>): JSX.Element => {
  return <NavContext.Provider value={value as Navigation}>{children}</NavContext.Provider>;
};
